<template>
  <div
    v-if="error"
    class="flex h-screen w-full items-center justify-center"
  >
    <progress-error :error-message="error" />
  </div>
  <loading-screen v-else-if="isLayoutPending" />
  <component
    :is="LayoutDefaultComponent"
    v-else-if="LayoutDefaultComponent"
  >
    <slot />
  </component>
</template>

<script lang="ts" setup>
import type { Component } from 'vue'
import { onUnmounted, ref, shallowRef } from 'vue'

import { ProgressError } from '@/shared/components/loaders'
import { LoadingScreen } from '@/shared/components/loading-screen'

import { resolveSuspenseErrorMessage } from '../lib/resolve-suspense-error-message'

const LAYOUT_LOAD_TIMEOUT_MS = 30_000

const error = ref('')
const isLayoutPending = ref(true)
const LayoutDefaultComponent = shallowRef<Component | null>(null)

const setError = (err: unknown) => {
  error.value = resolveSuspenseErrorMessage(err)
  isLayoutPending.value = false
}

const layoutLoadTimeoutId = setTimeout(() => {
  if (isLayoutPending.value) {
    setError(new Error('Layout load timeout'))
  }
}, LAYOUT_LOAD_TIMEOUT_MS)

void import('@/widgets/layout')
  .then(({ LayoutDefault }) => {
    if (!error.value) {
      LayoutDefaultComponent.value = LayoutDefault
    }
  })
  .catch(setError)
  .finally(() => {
    isLayoutPending.value = false
    clearTimeout(layoutLoadTimeoutId)
  })

onUnmounted(() => {
  clearTimeout(layoutLoadTimeoutId)
})
</script>
